/**
 * Message Content Component
 * Renders message content with proper formatting, attachments, and deleted message handling
 */

import * as React from "react";
import { Image } from "lucide-react";
import { cn } from "@/lib/utils";
import { linkifyUrls } from "@/lib/url-linkify";
import DOMPurify from "dompurify";

interface MessageContentProps {
  content?: string;
  contentAttributes?: Record<string, unknown> | string;
  additionalAttributes?: Record<string, unknown> | string;
  className?: string;
  contentType?: string;
  highlightKeywords?: string[];
}

/**
 * Parse and clean message content
 * Handles formatting tags like [text:color=...] and renders them properly
 */
const parseMessageContent = (content: string): string => {
  if (!content) return "";

  // Remove formatting tags like [text:color=#ffffff,background=#000000] and [/text]
  const cleaned = content
    .replace(/\[text:[^\]]*\]/g, "") // Remove opening text tags with attributes
    .replace(/\[\/text\]/g, "") // Remove closing text tags
    .trim();

  return cleaned;
};

const highlightKeywordsInContent = (
  content: string,
  keywords?: string[],
): string => {
  if (!keywords || keywords.length === 0) return content;

  let highlightedContent = content;
  keywords.forEach((keyword) => {
    if (!keyword) return;

    // Create a case-insensitive regex to match the keyword
    // Use word boundaries for better matching
    const regex = new RegExp(
      `(${keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi",
    );

    // Replace matches with highlighted version
    highlightedContent = highlightedContent.replace(
      regex,
      '<mark class="bg-amber-200 text-black-50 px-0.5">$1</mark>',
    );
  });

  return highlightedContent;
};

/**
 * Parse JSON string to object if needed
 */
const parseAttributes = (
  attrs: unknown,
): Record<string, unknown> | undefined => {
  if (!attrs) return undefined;
  if (typeof attrs === "string") {
    try {
      return JSON.parse(attrs) as Record<string, unknown>;
    } catch {
      return undefined;
    }
  }
  if (typeof attrs === "object" && attrs !== null) {
    return attrs as Record<string, unknown>;
  }
  return undefined;
};

/**
 * Extract image attachments from content
 */
const extractImages = (
  content: string,
  contentAttributes?: Record<string, unknown> | string,
): string[] => {
  const images: string[] = [];

  // Extract markdown image syntax: ![](url)
  const markdownImageRegex = /!\[.*?\]\((.*?)\)/g;
  let match;
  while ((match = markdownImageRegex.exec(content)) !== null) {
    if (match[1]) {
      images.push(match[1]);
    }
  }

  // Parse contentAttributes if it's a string
  const parsedAttrs = parseAttributes(contentAttributes);

  // Also check contentAttributes for attachments
  if (parsedAttrs?.attachments && Array.isArray(parsedAttrs.attachments)) {
    (parsedAttrs.attachments as []).forEach(
      (attachment: {
        file_url: string | undefined;
        data_url: string | undefined;
      }) => {
        if (attachment.file_url) {
          images.push(attachment.file_url as string);
        } else if (attachment.data_url) {
          images.push(attachment.data_url as string);
        }
      },
    );
  }

  return images;
};

/**
 * Check if message is deleted
 */
const isMessageDeleted = (
  content?: string,
  additionalAttributes?: Record<string, unknown> | string,
): boolean => {
  if (!content) return false;
  const lowerContent = content.toLowerCase();

  // Parse additionalAttributes if it's a string
  const parsedAttrs = parseAttributes(additionalAttributes);

  return (
    lowerContent.includes("this message was deleted") ||
    lowerContent === "deleted" ||
    parsedAttrs?.deleted === true ||
    parsedAttrs?.is_deleted === true
  );
};

/**
 * Check if message is an email
 * Checks contentType, channel_type, or presence of email in contentAttributes
 */
const isEmailMessage = (
  contentType?: string,
  contentAttributes?: Record<string, unknown> | string,
  additionalAttributes?: Record<string, unknown> | string,
): boolean => {
  // Check contentType first
  if (contentType === "incoming_email") {
    return true;
  }

  // Parse attributes if they are JSON strings
  const parsedContentAttrs = parseAttributes(contentAttributes);
  const parsedAdditionalAttrs = parseAttributes(additionalAttributes);

  // Check channel_type in additionalAttributes
  if (parsedAdditionalAttrs?.channel_type === "email") {
    return true;
  }

  // Check if contentAttributes.email exists (indicates email structure)
  if (parsedContentAttrs?.email) {
    return true;
  }

  return false;
};

/**
 * Extract email fields from attributes
 * Matches Candy's structure: contentAttributes.email with from/to/cc/bcc as arrays
 */
const extractEmailFields = (
  contentAttributes?: Record<string, unknown> | string,
  additionalAttributes?: Record<string, unknown> | string,
): {
  subject?: string;
  from?: string[];
  to?: string[];
  cc?: string[];
  bcc?: string[];
  date?: string;
  htmlContent?: string;
  textContent?: string;
} => {
  // Parse attributes if they are JSON strings
  const parsedContentAttrs = parseAttributes(contentAttributes);
  const parsedAdditionalAttrs = parseAttributes(additionalAttributes);

  // Check for email in contentAttributes first (Candy's structure)
  const emailData =
    (parsedContentAttrs?.email as Record<string, unknown>) || {};

  // Fallback to direct attributes for backward compatibility
  const attrs = { ...parsedContentAttrs, ...parsedAdditionalAttrs };

  // Extract from email object (Candy structure)
  const emailFrom = emailData.from;
  const emailTo = emailData.to;
  const emailCc = emailData.cc;
  const emailBcc = emailData.bcc;
  const emailSubject = emailData.subject;
  const htmlContent = (emailData.html_content as { full?: string })?.full;
  const textContent = (emailData.text_content as { full?: string })?.full;

  // Helper to normalize email addresses to array format
  const normalizeToArray = (value: unknown): string[] | undefined => {
    if (!value) return undefined;
    if (Array.isArray(value)) {
      return value.map((item) => {
        if (typeof item === "string") return item;
        if (typeof item === "object" && item !== null) {
          const obj = item as { name?: string; email?: string };
          if (obj.email) {
            return obj.name ? `${obj.name} <${obj.email}>` : obj.email;
          }
        }
        return String(item);
      });
    }
    if (typeof value === "string") {
      return [value];
    }
    if (typeof value === "object" && value !== null) {
      const obj = value as { name?: string; email?: string };
      if (obj.email) {
        return [obj.name ? `${obj.name} <${obj.email}>` : obj.email];
      }
    }
    return undefined;
  };

  return {
    subject:
      typeof emailSubject === "string"
        ? emailSubject
        : typeof attrs.subject === "string"
          ? attrs.subject
          : undefined,
    from: normalizeToArray(emailFrom) || normalizeToArray(attrs.from),
    to: normalizeToArray(emailTo) || normalizeToArray(attrs.to),
    cc: normalizeToArray(emailCc) || normalizeToArray(attrs.cc),
    bcc: normalizeToArray(emailBcc) || normalizeToArray(attrs.bcc),
    date:
      typeof attrs.date === "string"
        ? attrs.date
        : typeof attrs.timestamp === "string"
          ? attrs.timestamp
          : undefined,
    htmlContent: typeof htmlContent === "string" ? htmlContent : undefined,
    textContent: typeof textContent === "string" ? textContent : undefined,
  };
};

/**
 * Format email addresses array for display
 */
const formatEmailAddresses = (addresses?: string[]): string => {
  if (!addresses || addresses.length === 0) return "";
  return addresses.join(", ");
};

/**
 * Email display component
 * Matches Candy's email display structure - integrated into message bubble
 */
const EmailDisplay = ({
  content,
  emailFields,
  images,
  highlightKeywords,
}: {
  content: string;
  emailFields: ReturnType<typeof extractEmailFields>;
  images: string[];
  highlightKeywords?: string[];
}) => {
  // Use HTML content if available, otherwise text content, otherwise parsed content
  const isHtmlContent = !!emailFields.htmlContent;
  const emailContent =
    emailFields.htmlContent || emailFields.textContent || content;
  // Only parse text content, not HTML
  let cleanedContent = isHtmlContent
    ? emailContent
    : parseMessageContent(emailContent);

  // Apply keyword highlighting
  if (highlightKeywords && highlightKeywords.length > 0) {
    if (isHtmlContent) {
      cleanedContent = highlightKeywordsInContent(
        cleanedContent,
        highlightKeywords,
      );
    } else {
      // For text content, apply highlighting after linkification
      cleanedContent = linkifyUrls(cleanedContent);
      cleanedContent = highlightKeywordsInContent(
        cleanedContent,
        highlightKeywords,
      );
    }
  }
  const hasEmailHeaders =
    emailFields.from ||
    emailFields.to ||
    emailFields.cc ||
    emailFields.bcc ||
    emailFields.subject;

  return (
    <p className="w-full min-w-0 whitespace-pre-wrap wrap-break-word max-w-fit">
      {/* Email Header - Matches Candy's MailHead style */}
      {hasEmailHeaders && (
        <div className="pb-2 mb-2 border-b border-gray-300 space-y-1">
          {emailFields.from && emailFields.from.length > 0 && (
            <div className="text-base">
              <span className="font-semibold">From:</span>
              <span className="ml-1">
                {formatEmailAddresses(emailFields.from)}
              </span>
            </div>
          )}
          {emailFields.to && emailFields.to.length > 0 && (
            <div className="text-xs">
              <span className="font-semibold">To:</span>
              <span className="ml-1">
                {formatEmailAddresses(emailFields.to)}
              </span>
            </div>
          )}
          {emailFields.cc && emailFields.cc.length > 0 && (
            <div className="text-xs">
              <span className="font-semibold">Cc:</span>
              <span className="ml-1">
                {formatEmailAddresses(emailFields.cc)}
              </span>
            </div>
          )}
          {emailFields.bcc && emailFields.bcc.length > 0 && (
            <div className="text-xs">
              <span className="font-semibold">Bcc:</span>
              <span className="ml-1">
                {formatEmailAddresses(emailFields.bcc)}
              </span>
            </div>
          )}
          {emailFields.subject && (
            <div className="text-xs">
              <span className="font-semibold">Subject:</span>
              <span className="ml-1">{emailFields.subject}</span>
            </div>
          )}
        </div>
      )}

      {/* Email Body - Rendered directly in bubble like Candy */}
      <div className="space-y-2 min-w-0">
        {cleanedContent && (
          <div
            className="w-full text-xs prose prose-sm [&_a.link]:text-primary [&_a.link]:underline [&_a.link]:hover:text-primary/80 [&_a.link]:transition-colors [&_a]:text-primary [&_a]:underline [&_*]:max-w-full [&_img]:h-auto [&_table]:block [&_table]:overflow-x-auto [&_pre]:whitespace-pre-wrap [&_code]:whitespace-pre-wrap"
            style={{
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              maxWidth: "100%",
              overflow: "hidden",
            }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(cleanedContent, {
                ADD_TAGS: ["mark"],
                ADD_ATTR: ["class"],
              }),
            }}
          />
        )}
        {images.length > 0 && (
          <div className="space-y-2 pt-2">
            {images.map((imageUrl, index) => (
              <ImageWithFallback
                key={index}
                imageUrl={imageUrl}
                index={index}
              />
            ))}
          </div>
        )}
      </div>
    </p>
  );
};

/**
 * Image component with fallback for failed loads
 */
const ImageWithFallback = ({
  imageUrl,
  index,
}: {
  imageUrl: string;
  index: number;
}) => {
  const [imageError, setImageError] = React.useState(false);

  if (imageError) {
    return (
      <div className="flex items-center gap-2 p-3 bg-muted rounded-lg border border-border">
        <Image className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Image attachment</span>
        <a
          href={imageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary hover:underline ml-auto"
        >
          View
        </a>
      </div>
    );
  }

  return (
    <div className="relative rounded-lg overflow-hidden border border-border max-w-full">
      <img
        src={imageUrl}
        alt={`Attachment ${index + 1}`}
        className="max-w-full h-auto object-contain"
        onError={() => setImageError(true)}
      />
    </div>
  );
};

export const MessageContent = ({
  content,
  contentAttributes,
  additionalAttributes,
  className,
  contentType,
  highlightKeywords,
}: MessageContentProps) => {
  const deleted = isMessageDeleted(content, additionalAttributes);
  const images = extractImages(content || "", contentAttributes);
  const cleanedContent = parseMessageContent(content || "");

  const isEmail = isEmailMessage(
    contentType,
    contentAttributes,
    additionalAttributes,
  );
  const emailFields = isEmail
    ? extractEmailFields(contentAttributes, additionalAttributes)
    : null;
  if (deleted) {
    return (
      <p
        className={cn(
          "text-muted-foreground italic text-sm whitespace-pre-wrap wrap-break-word ",
          className,
        )}
      >
        <span className="opacity-60">This message was deleted</span>
      </p>
    );
  }

  // Render email format if it's an email message
  if (isEmail && content) {
    return (
      <div className={cn("min-w-0 w-full", className)}>
        <EmailDisplay
          content={content}
          emailFields={emailFields || {}}
          images={images}
          highlightKeywords={highlightKeywords}
        />
      </div>
    );
  }

  if (!cleanedContent && images.length === 0) {
    return (
      <p
        className={cn(
          "text-muted-foreground text-sm whitespace-pre-wrap wrap-break-word",
          className,
        )}
      >
        <span className="opacity-60 text-xs">Empty message</span>
      </p>
    );
  }

  // Linkify URLs in the content
  let linkifiedContent = linkifyUrls(cleanedContent);

  // Apply keyword highlighting AFTER linkification
  if (highlightKeywords && highlightKeywords.length > 0) {
    linkifiedContent = highlightKeywordsInContent(
      linkifiedContent,
      highlightKeywords,
    );
  }

  return (
    <div className={cn("space-y-2 min-w-0 w-full", className)}>
      {linkifiedContent && (
        <div
          className="whitespace-pre-wrap wrap-break-word w-full prose prose-sm [&_a.link]:text-primary [&_a.link]:underline [&_a.link]:hover:text-primary/80 [&_a.link]:transition-colors [&_*]:max-w-full [&_img]:h-auto [&_pre]:whitespace-pre-wrap [&_code]:whitespace-pre-wrap"
          style={{
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            maxWidth: "100%",
            overflow: "hidden",
          }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(linkifiedContent, {
              ADD_TAGS: ["mark"],
              ADD_ATTR: ["class"],
            }),
          }}
        />
      )}
      {images.length > 0 && (
        <div className="space-y-2 mt-2 w-full min-w-0">
          {images.map((imageUrl, index) => (
            <ImageWithFallback key={index} imageUrl={imageUrl} index={index} />
          ))}
        </div>
      )}
    </div>
  );
};
